/**
 * SitePick front — `/compare/` ページ（docs/06 §5）
 *
 * compare ストアの ID → `POST /sites/batch` → 比較表示。追加順を維持。
 * `?ids=` があれば「置換」（現在の選択があれば確認 UI）。変更で `history.replaceState`。
 * 「与えたい印象」セレクタは一致タームをハイライトするだけ（保存しない）。
 * SP は横スクロール（CSS）。
 */

import { compareStore } from "./compare-store.js";
import { fetchBatchSites, SiteApiError } from "./site-api.js";
import { sanitizeIds, sameIds } from "./sanitize.js";
import { viewComparison, siteToItem } from "./analytics.js";
import { priceBlock } from "./price-block.js";
import { lockNoticeFor } from "./lock-notice.js";
import { holdNoticeText, isHoldState } from "./hold-notice.js";

let pageEl = null;
let bodyEl = null;
let stateEl = null;
let impressionSelect = null;
let selectedImpression = "";
let rendering = false;
let queued = false;

const ROWS = [
  { key: "visual", label: "ビジュアル" },
  { key: "status", label: "販売状態" },
  { key: "price", label: "価格" },
  { key: "industry", label: "業種" },
  { key: "purpose", label: "目的" },
  { key: "impression", label: "印象" },
  { key: "page_count", label: "ページ数" },
  { key: "volume_level", label: "情報量" },
  { key: "motion_level", label: "アニメーション量" },
  { key: "customizable", label: "カスタマイズ範囲" },
  { key: "palette", label: "色調" },
  { key: "live", label: "デモ" },
  { key: "detail", label: "申込み・詳細" },
];

function messagesObj() {
  return (window.SITEPICK && window.SITEPICK.messages) || {};
}

function msg(key, fallback) {
  const m = messagesObj();
  return typeof m[key] === "string" && m[key] ? m[key] : fallback;
}

function el(tag, className, text) {
  const node = document.createElement(tag);
  if (className) {
    node.className = className;
  }
  if (text !== undefined && text !== null) {
    node.textContent = String(text);
  }
  return node;
}

function levelLabel(value) {
  switch (value) {
    case "low":
      return msg("levelLow", "少なめ");
    case "mid":
      return msg("levelMid", "標準");
    case "high":
      return msg("levelHigh", "多め");
    default:
      return "—";
  }
}

// 価格の描画は `price-block.js` に集約（PHP の共通 renderer と同じマークアップ）。
// **割引の計算はしない** —— サーバー（PriceResolver）が返した値をそのまま出す。
function priceNodeFor(site) {
  return priceBlock(site, msg("priceTbd", "価格準備中"));
}

function statusLabel(status) {
  switch (status) {
    case "AVAILABLE":
      return msg("statusAvailable", "購入可能");
    case "RESERVED":
      return msg("statusReserved", "手続き中");
    case "HELD":
      return msg("statusHeld", "取置き中");
    case "SOLD":
      return msg("statusSold", "SOLD");
    default:
      return msg("statusDraft", "販売前");
  }
}

/** `?ids=` を読む。 */
function idsFromQuery() {
  try {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get("ids");
    if (!raw) {
      return [];
    }
    return sanitizeIds(
      raw.split(",").map((s) => parseInt(s.trim(), 10)),
      compareStore.max()
    );
  } catch {
    return [];
  }
}

/** 現在の compare を `?ids=` に反映（履歴を汚さない）。 */
function syncQuery() {
  try {
    const ids = compareStore.get();
    const url = new URL(window.location.href);
    if (ids.length > 0) {
      url.searchParams.set("ids", ids.join(","));
    } else {
      url.searchParams.delete("ids");
    }
    window.history.replaceState(null, "", url);
  } catch {
    /* ignore */
  }
}

function showState(name, message) {
  if (!stateEl) {
    return;
  }
  stateEl.hidden = false;
  stateEl.dataset.spState = name;
  const text = stateEl.querySelector("[data-sp-state-text]");
  if (text && message) {
    text.textContent = message;
  }
  const retry = stateEl.querySelector("[data-sp-retry]");
  if (retry) {
    retry.hidden = name !== "error";
  }
}

function hideState() {
  if (stateEl) {
    stateEl.hidden = true;
  }
}

/**
 * 1 行分のセル値 DOM。
 *
 * @param {string} key
 * @param {Object} site
 * @returns {HTMLElement}
 */
function cell(key, site) {
  const td = el("td", "p-compare__cell");
  switch (key) {
    case "visual": {
      if (site.thumb) {
        const img = el("img", "p-compare__thumb");
        img.src = site.thumb;
        img.alt = site.name || "";
        img.loading = "lazy";
        td.appendChild(img);
      } else {
        td.appendChild(el("span", "p-compare__thumb p-compare__thumb--placeholder"));
      }
      const name = el("a", "p-compare__name", site.name || "");
      name.href = site.url || "#";
      td.appendChild(name);
      break;
    }
    case "status":
      // 状態ラベルは SOLD だけバッジ（parts/compare-table.php と同じ）。「購入可能」「手続き中」はバッジにしない。
      // RESERVED は残り分（サーバー値）を添えた案内文。
      // HELD と RESERVED + hold_active は取置きの短い文（あと約 N 時間）。「約 N 分後」は出さない。
      if (site.status === "SOLD") {
        td.appendChild(el("span", "c-badge is-sold", statusLabel("SOLD")));
      } else if (site.status === "HELD" || isHoldState(site)) {
        td.appendChild(el("span", "p-compare__status", holdNoticeText(site, messagesObj(), "card")));
      } else if (site.status === "RESERVED") {
        td.appendChild(el("span", "p-compare__status", lockNoticeFor(site)));
      } else {
        td.appendChild(el("span", "p-compare__status", statusLabel(site.status)));
      }
      break;
    case "price":
      td.appendChild(priceNodeFor(site));
      break;
    case "industry":
    case "purpose":
    case "impression": {
      const terms = Array.isArray(site[key]) ? site[key] : [];
      if (terms.length === 0) {
        td.textContent = "—";
        break;
      }
      const ul = el("ul", "p-compare__terms");
      terms.forEach((term) => {
        const liClass =
          key === "impression" && selectedImpression && term.slug === selectedImpression
            ? "p-compare__term is-match"
            : "p-compare__term";
        ul.appendChild(el("li", liClass, term.name || term.slug || ""));
      });
      td.appendChild(ul);
      break;
    }
    case "page_count":
      td.textContent = Number.isInteger(site.page_count) ? `${site.page_count}` : "—";
      break;
    case "volume_level":
    case "motion_level":
      td.textContent = levelLabel(site[key]);
      break;
    case "customizable": {
      const list = Array.isArray(site.customizable) ? site.customizable : [];
      if (list.length === 0) {
        td.textContent = "—";
        break;
      }
      const ul = el("ul", "p-compare__list");
      list.forEach((item) => ul.appendChild(el("li", null, item)));
      td.appendChild(ul);
      break;
    }
    case "palette":
      td.textContent =
        Array.isArray(site.palette) && site.palette.length
          ? site.palette.join(" / ")
          : "—";
      break;
    case "live": {
      const a = el("a", "c-link", msg("viewLive", "実物を見る"));
      a.href = site.live_url || site.url || "#";
      td.appendChild(a);
      break;
    }
    case "detail": {
      const a = el("a", "c-button c-button--secondary", msg("viewDetail", "詳細を見る"));
      a.href = site.url || "#";
      td.appendChild(a);
      break;
    }
    default:
      td.textContent = "—";
  }
  return td;
}

/**
 * 比較表を組む。
 *
 * @param {Object[]} sites 追加順
 */
function renderTable(sites) {
  bodyEl.textContent = "";

  const scroll = el("div", "p-compare__scroll");
  const table = el("table", "p-compare__table");

  // ヘッダ行（商品名 + 各列の解除）
  const thead = el("thead");
  const headRow = el("tr");
  headRow.appendChild(el("th", "p-compare__rowhead", ""));
  sites.forEach((site) => {
    const th = el("th", "p-compare__colhead");
    th.scope = "col";
    th.appendChild(el("span", "p-compare__colname", site.name || ""));
    const remove = el("button", "p-compare__remove js-sp-compare is-active");
    remove.type = "button";
    remove.setAttribute("aria-pressed", "true");
    remove.setAttribute("data-sp-id", String(site.id));
    remove.setAttribute("aria-label", msg("compareRemoveLabel", "%s を比較から削除").replace("%s", site.name || `#${site.id}`));
    remove.textContent = "×";
    th.appendChild(remove);
    headRow.appendChild(th);
  });
  thead.appendChild(headRow);
  table.appendChild(thead);

  const tbody = el("tbody");
  ROWS.forEach((row) => {
    const tr = el("tr");
    const th = el("th", "p-compare__rowhead", row.label);
    th.scope = "row";
    tr.appendChild(th);
    sites.forEach((site) => tr.appendChild(cell(row.key, site)));
    tbody.appendChild(tr);
  });
  table.appendChild(tbody);

  scroll.appendChild(table);
  bodyEl.appendChild(scroll);
}

/**
 * 印象セレクタを商品群の impression から組む。
 */
function buildImpressionOptions(sites) {
  if (!impressionSelect) {
    return;
  }
  const seen = new Map();
  sites.forEach((site) => {
    (Array.isArray(site.impression) ? site.impression : []).forEach((term) => {
      if (term && term.slug && !seen.has(term.slug)) {
        seen.set(term.slug, term.name || term.slug);
      }
    });
  });
  const current = impressionSelect.value;
  impressionSelect.textContent = "";
  impressionSelect.appendChild(el("option", null, msg("impressionAny", "指定なし"))).value = "";
  seen.forEach((name, slug) => {
    const opt = el("option", null, name);
    opt.value = slug;
    impressionSelect.appendChild(opt);
  });
  if (current && seen.has(current)) {
    impressionSelect.value = current;
  }
}

async function render() {
  if (rendering) {
    queued = true;
    return;
  }
  rendering = true;

  const ids = compareStore.get();
  syncQuery();

  if (ids.length === 0) {
    bodyEl.textContent = "";
    showState("empty", msg("compareEmpty", "比較するサイトが選択されていません。"));
    rendering = false;
    return;
  }

  showState("loading", msg("loading", "読み込み中…"));

  let sites;
  try {
    sites = await fetchBatchSites(ids);
  } catch (err) {
    showState(
      "error",
      err instanceof SiteApiError && err.kind === "rate_limited"
        ? msg("rateLimited", "アクセスが集中しています。しばらくして再度お試しください。")
        : msg("error", "エラーが発生しました。時間をおいて再度お試しください。")
    );
    rendering = false;
    if (queued) {
      queued = false;
      render();
    }
    return;
  }

  const byId = new Map();
  sites.forEach((s) => {
    if (s && Number.isInteger(s.id)) {
      byId.set(s.id, s);
    }
  });
  const ordered = ids.filter((id) => byId.has(id)).map((id) => byId.get(id));

  const keep = ids.filter((id) => byId.has(id));
  if (keep.length !== ids.length) {
    compareStore.retainOnly(keep);
  }

  if (ordered.length === 0) {
    bodyEl.textContent = "";
    showState("empty", msg("compareEmpty", "比較するサイトが選択されていません。"));
  } else {
    hideState();
    buildImpressionOptions(ordered);
    renderTable(ordered);
    viewComparison(
      ordered.map((s, i) => siteToItem(s, i + 1)),
      selectedImpression
    );
  }

  rendering = false;
  if (queued) {
    queued = false;
    render();
  }
}

/**
 * `?ids=` を初期取り込み（置換。現在選択があれば確認）。
 */
function adoptQueryIds() {
  const q = idsFromQuery();
  if (q.length === 0) {
    return;
  }
  const current = compareStore.get();
  if (sameIds(q, current)) {
    return;
  }
  if (current.length > 0) {
    const ok = window.confirm(
      msg("compareReplaceConfirm", "共有リンクの内容で現在の比較を置き換えますか？")
    );
    if (!ok) {
      return;
    }
  }
  compareStore.clear();
  q.forEach((id) => compareStore.add(id));
}

/**
 * 初期化。
 *
 * @returns {void}
 */
export function initComparePage() {
  pageEl = document.querySelector('[data-sp-page="compare"]');
  if (!pageEl) {
    return;
  }
  bodyEl = pageEl.querySelector("[data-sp-compare-root]");
  stateEl = pageEl.querySelector("[data-sp-compare-state]");
  impressionSelect = pageEl.querySelector("[data-sp-impression-select]");

  if (impressionSelect) {
    impressionSelect.addEventListener("change", () => {
      selectedImpression = impressionSelect.value || "";
      render();
    });
  }

  const retry = pageEl.querySelector("[data-sp-retry]");
  if (retry) {
    retry.addEventListener("click", () => render());
  }

  adoptQueryIds();
  compareStore.subscribe(() => render());
  render();
}
